import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend,
} from "recharts";
import { Eye, MousePointerClick, Users, TrendingUp, ExternalLink, Loader2 } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { fetchAnalytics } from "@/lib/api";

const COLORS = ["#A259FF", "#FF62A5", "#FF8A3D", "#0071E3", "#34C759", "#FF9500", "#FF3B30", "#5AC8FA"];

const StatCard = ({ icon: Icon, label, value, sub, accent, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 14 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay }}
    className="bg-white rounded-2xl border border-black/5 shadow-[0_2px_10px_rgba(0,0,0,0.02)] p-5"
  >
    <div className="flex items-center justify-between mb-3">
      <span className="text-xs font-semibold text-[#86868B] uppercase tracking-wide">{label}</span>
      <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: `${accent}18` }}>
        <Icon className="h-4 w-4" style={{ color: accent }} strokeWidth={2.5} />
      </div>
    </div>
    <p className="font-heading text-3xl font-bold text-[#1D1D1F]">{value?.toLocaleString("de-DE") ?? "—"}</p>
    {sub && <p className="text-xs text-[#86868B] mt-1">{sub}</p>}
  </motion.div>
);

const formatDate = (d) => {
  if (!d) return "";
  const parts = d.split("-");
  return `${parts[2]}.${parts[1]}`;
};

export default function Analytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics().then(setData).catch(console.error).finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="flex items-center justify-center h-96">
      <Loader2 className="h-8 w-8 animate-spin text-[#A259FF]" />
    </div>
  );

  if (!data) return (
    <div className="p-10 text-center text-[#86868B]">Keine Daten verfügbar</div>
  );

  // Chart-Daten kombinieren (Views + Clicks)
  const combinedChart = data.daily_chart.map((d, i) => ({
    date: formatDate(d.date),
    views: d.views,
    clicks: data.clicks_chart[i]?.clicks || 0,
  }));

  return (
    <div data-testid="analytics-page">
      <PageHeader title="Statistiken" subtitle="Besucher, Klicks und Deal-Performance auf einen Blick" />

      <div className="px-6 md:px-10 py-8 space-y-6">

        {/* KPI Cards */}
        <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
          <StatCard icon={Eye} label="Seitenaufrufe heute" value={data.pageviews.today} sub={`${data.pageviews.last_7_days} letzte 7 Tage`} accent="#0071E3" delay={0} />
          <StatCard icon={Users} label="Unique Visitors heute" value={data.unique_visitors.today} sub={`${data.unique_visitors.last_7_days} letzte 7 Tage`} accent="#A259FF" delay={0.06} />
          <StatCard icon={MousePointerClick} label="Deal-Klicks heute" value={data.clicks.today} sub={`${data.clicks.last_7_days} letzte 7 Tage`} accent="#FF62A5" delay={0.12} />
          <StatCard icon={TrendingUp} label="Klicks (30 Tage)" value={data.clicks.last_30_days} sub={`${data.pageviews.last_30_days} Views (30 Tage)`} accent="#34C759" delay={0.18} />
        </div>

        {/* Hauptchart: Besucher + Klicks */}
        <motion.div
          initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
          className="bg-white rounded-2xl border border-black/5 shadow-[0_2px_10px_rgba(0,0,0,0.02)] p-6"
        >
          <h2 className="font-heading text-lg font-bold text-[#1D1D1F] mb-6">Besucher & Klicks — letzte 14 Tage</h2>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={combinedChart} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="gradViews" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#0071E3" stopOpacity={0.15} />
                  <stop offset="95%" stopColor="#0071E3" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gradClicks" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#A259FF" stopOpacity={0.15} />
                  <stop offset="95%" stopColor="#A259FF" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#86868B" }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#86868B" }} tickLine={false} axisLine={false} />
              <Tooltip
                contentStyle={{ borderRadius: 12, border: "1px solid #F0F0F0", boxShadow: "0 4px 20px rgba(0,0,0,0.08)" }}
                labelStyle={{ fontWeight: 600 }}
              />
              <Legend iconType="circle" iconSize={8} wrapperStyle={{ paddingTop: 16, fontSize: 12 }} />
              <Area type="monotone" dataKey="views" name="Seitenaufrufe" stroke="#0071E3" strokeWidth={2} fill="url(#gradViews)" dot={false} />
              <Area type="monotone" dataKey="clicks" name="Deal-Klicks" stroke="#A259FF" strokeWidth={2} fill="url(#gradClicks)" dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        </motion.div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">

          {/* Top geklickte Deals */}
          <motion.div
            initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
            className="bg-white rounded-2xl border border-black/5 shadow-[0_2px_10px_rgba(0,0,0,0.02)] p-6"
          >
            <h2 className="font-heading text-lg font-bold text-[#1D1D1F] mb-4">Top geklickte Deals</h2>
            {data.top_clicked_deals.length === 0 ? (
              <div className="text-center py-10 text-[#86868B] text-sm">
                Noch keine Klicks — besuche die Seite und klicke auf Deals!
              </div>
            ) : (
              <div className="space-y-2">
                {data.top_clicked_deals.map((d, i) => (
                  <div key={d.id} className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-[#F5F5F7] transition-colors group">
                    <span className="text-sm font-bold text-[#A1A1A6] w-5 shrink-0">{i + 1}</span>
                    {d.image_url ? (
                      <img src={d.image_url} alt="" className="h-10 w-10 rounded-lg object-cover bg-[#F5F5F7] shrink-0" />
                    ) : (
                      <div className="h-10 w-10 rounded-lg bg-[#F5F5F7] shrink-0" />
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-[#1D1D1F] truncate">{d.title}</p>
                      <p className="text-xs text-[#86868B]">{d.category} · -{d.discount_percent}% · {d.deal_price?.toFixed(2)}€</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-sm font-bold text-[#A259FF]">{d.clicks}</span>
                      <span className="text-xs text-[#86868B]">Klicks</span>
                      {d.affiliate_url && (
                        <a href={d.affiliate_url} target="_blank" rel="noopener noreferrer"
                          className="opacity-0 group-hover:opacity-100 transition-opacity">
                          <ExternalLink className="h-3.5 w-3.5 text-[#86868B]" />
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </motion.div>

          {/* Klicks nach Kategorie — Pie + Bar */}
          <motion.div
            initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.36 }}
            className="bg-white rounded-2xl border border-black/5 shadow-[0_2px_10px_rgba(0,0,0,0.02)] p-6"
          >
            <h2 className="font-heading text-lg font-bold text-[#1D1D1F] mb-4">Klicks nach Kategorie</h2>
            {data.clicks_by_category.length === 0 ? (
              <div className="text-center py-10 text-[#86868B] text-sm">Noch keine Kategorie-Daten</div>
            ) : (
              <>
                <ResponsiveContainer width="100%" height={180}>
                  <PieChart>
                    <Pie data={data.clicks_by_category} dataKey="clicks" nameKey="category"
                      cx="50%" cy="50%" outerRadius={70} innerRadius={40} paddingAngle={3}>
                      {data.clicks_by_category.map((_, i) => (
                        <Cell key={i} fill={COLORS[i % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #F0F0F0" }} />
                    <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="mt-4 space-y-2">
                  {data.clicks_by_category.slice(0, 5).map((c, i) => (
                    <div key={c.category} className="flex items-center gap-2">
                      <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: COLORS[i % COLORS.length] }} />
                      <span className="text-xs text-[#1D1D1F] flex-1 truncate">{c.category}</span>
                      <span className="text-xs font-bold text-[#1D1D1F]">{c.clicks}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </motion.div>
        </div>

        {/* Klick-Balkendiagramm */}
        <motion.div
          initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}
          className="bg-white rounded-2xl border border-black/5 shadow-[0_2px_10px_rgba(0,0,0,0.02)] p-6"
        >
          <h2 className="font-heading text-lg font-bold text-[#1D1D1F] mb-6">Tägliche Klicks — letzte 14 Tage</h2>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={combinedChart} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#86868B" }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#86868B" }} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #F0F0F0", boxShadow: "0 4px 20px rgba(0,0,0,0.08)" }} />
              <Bar dataKey="clicks" name="Klicks" fill="#A259FF" radius={[4, 4, 0, 0]} maxBarSize={40} />
            </BarChart>
          </ResponsiveContainer>
        </motion.div>

      </div>
    </div>
  );
}
